import { HbrMenuDivider, HbrMenuItem, HbrMenuLabel } from '@harbor/elements-react-standalone';
import React from "react";
import { useSelector } from "react-redux";
import sideNavStyle from '../components/security-toolbox-ui/StbSideNavigation.style';
import StbSideNavigationItem from '../components/security-toolbox-ui/StbSideNavigationItem';
import { RouteHistoryEntry } from "../support/slices/uiSlice";
import { RootState } from "../support/store";

const normalizeBaseUrl = (baseUrl: string) => {
  if (!baseUrl) return '/';
  if (baseUrl === '/') return '/';
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
};

/**
 * Same fallback chain StbSideNavigationItem uses, so a recent entry reads the same whichever
 * variant renders it.
 */
const getRecentLabel = (historyItem: RouteHistoryEntry) =>
  (
    historyItem.title ??
    historyItem.params?.section ??
    historyItem.route.replace('/', ' ')
  ).replace('-', ' ');

/**
 * Where the recents list is being rendered:
 *
 *   'sidenav' - the collapsible left rail (StbSideNavigation), which styles its rows itself.
 *   'menu'    - an <HbrMenu> dropdown in the portal header, where recents has to be built from
 *               the same Harbor primitives as the Domain/Incubation groups next to it. Raw divs
 *               there rendered as bold, over-indented rows that stood out from every other
 *               option in the menu, and were skipped by the menu's keyboard navigation.
 */
export type RecentsMenuVariant = 'sidenav' | 'menu';

const RecentsMenuItems = ({
                            baseUrl,
                            handleClick,
                            currentRoute,
                            variant = 'sidenav',
                            showDivider = true
                          }: {
  baseUrl: string;
  handleClick: (value: string, path?: string) => void;
  currentRoute: string;
  variant?: RecentsMenuVariant;
  showDivider?: boolean;
  dividerClassName?: string;
}) => {
  const { routeHistory } = useSelector((state: RootState) => state.ui);

  const normalizedBase = normalizeBaseUrl(baseUrl);

  const recentItems = [...(routeHistory ?? [])]
    .slice(-12)
    .reverse()
    .filter((historyItem) => {
      if (!historyItem?.route) return false;

      if (normalizedBase === '/') {
        return historyItem.route === '/';
      }

      return (
        historyItem.route === normalizedBase ||
        historyItem.route.startsWith(`${normalizedBase}/`)
      );
    });

  if (!recentItems.length) {
    return null;
  }

  if (variant === 'menu') {
    return (
      <>
        {showDivider && <HbrMenuDivider />}

        <HbrMenuLabel>Recents</HbrMenuLabel>

        {recentItems.map((historyItem, i) => (
          <HbrMenuItem
            key={`recent-${historyItem.route}-${i}`}
            onClick={() => handleClick(historyItem.route)}
            className="capitalize"
          >
            {getRecentLabel(historyItem)}
          </HbrMenuItem>
        ))}
      </>
    );
  }

  return (
    <>
      {/* The same hairline the Domain/Incubation groups use above this one, rather than the
          zero-height div that used to sit here and draw nothing. */}
      {showDivider && <div className={sideNavStyle.separator}></div>}

      <h4 className="mx-6 mt-4 font-semibold text-sm">Recents</h4>

      {recentItems.map((historyItem, i) => (
        <StbSideNavigationItem
          navItem={historyItem}
          currentRoute={currentRoute}
          key={i}
          handleClick={() => handleClick(historyItem.route)}
        />
      ))}
    </>
  );
};

export default RecentsMenuItems;
